import cn from "@/v2-ui/utils/utils.cn"
import type {
  PublicPlatform,
  PublicServiceStatus,
} from "@/v2-status-client/status/status.types"
import StatusDot from "@/v2-status-client/status/dot/StatusDot"
import { STATUS_FILTER_ALL } from "@/v2-status-client/status/status.filter"

export type StatusPlatformNavProps = {
  platforms: PublicPlatform[]
  activePlatformId: string
  onSelect: (platformId: string) => void
}

// Horizontal pill nav. Each pill is one platform; an extra leading
// "All platforms" pill snaps the page back to the cross-platform
// overview. The nav is the page's primary navigation affordance
// after the redesign -- replaces the previous nested category
// accordion with a flat top-level scope picker that maps 1:1 onto
// the URL (?platform=<id>).
//
// Sticky positioning lives on the parent (`StatusStickyNav`) so
// this component stays layout-agnostic; if we ever embed the nav
// inside a dialog or a different page region, it still works.
//
// Status dot per pill so a customer can scan the pills without
// reading text and immediately see which platform is degraded; the
// "All" pill rolls up the worst status across every platform with
// the same intent.
function StatusPlatformNav(props: Readonly<StatusPlatformNavProps>) {
  const { platforms, activePlatformId, onSelect } = props
  const allStatus = aggregateStatus(platforms)

  return (
    <nav aria-label="Platform filter">
      <ul
        className={cn(
          // Horizontal scroll on narrow viewports rather than wrap;
          // wrapping pills look like a junk drawer of buttons.
          "m-0 flex list-none gap-1.5 overflow-x-auto py-2 [scrollbar-width:none]",
          "[&::-webkit-scrollbar]:hidden",
        )}
      >
        <li>
          <PillButton
            label="All platforms"
            count={platforms.length}
            status={allStatus}
            isActive={activePlatformId === STATUS_FILTER_ALL}
            onClick={() => onSelect(STATUS_FILTER_ALL)}
          />
        </li>
        {platforms.map((p) => (
          <li key={p.id}>
            <PillButton
              label={p.name}
              count={p.totalCount}
              status={p.status}
              isActive={activePlatformId === p.id}
              onClick={() => onSelect(p.id)}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}

type PillButtonProps = {
  label: string
  count: number
  status: PublicServiceStatus
  isActive: boolean
  onClick: () => void
}

// One pill. Rendered as a real <button> rather than an <a> because
// our setter goes through history.pushState directly -- if we used
// anchor tags we'd need to swallow the default click and the
// keyboard contract is the same either way.
function PillButton(props: Readonly<PillButtonProps>) {
  const { label, count, status, isActive, onClick } = props
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap rounded-full",
        "border px-3 py-1.5 text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isActive
          ? "border-foreground/40 bg-foreground/10 text-foreground"
          : "border-border/60 bg-background/40 text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      <StatusDot status={status} />
      <span>{label}</span>
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
          isActive
            ? "bg-foreground/15 text-foreground/80"
            : "bg-muted/50 text-muted-foreground/80",
        )}
      >
        {count}
      </span>
    </button>
  )
}

// aggregateStatus folds the per-platform statuses into a single
// status for the "All platforms" pill. Mirrors the backend's
// `aggregateStatus` rule for groups: red is reserved for the
// catastrophic case where *every* platform is down, amber covers
// any partial outage, and green is fully healthy. This keeps a
// single struggling platform from turning the top-level pill
// alarm-red when most of the rest of the fleet is fine.
function aggregateStatus(platforms: PublicPlatform[]): PublicServiceStatus {
  if (platforms.length === 0) return "operational"
  let downCount = 0
  let anyAttention = false
  for (const p of platforms) {
    if (p.status === "down") {
      downCount++
      anyAttention = true
    } else if (p.status === "degraded") {
      anyAttention = true
    }
  }
  if (downCount >= platforms.length) return "down"
  return anyAttention ? "degraded" : "operational"
}

export default StatusPlatformNav
